// PublicTrackingPage.jsx - Enhanced with new features
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

// Import existing components
import TrackingHeader from "../../components/public-tracking/TrackingHeader";
import TrackingFooter from "../../components/public-tracking/TrackingFooter";
import SearchForm from '../../components/public-tracking/SearchForm';
import LoadingState from '../../components/public-tracking/LoadingState';
import ErrorMessage from '../../components/public-tracking/ErrorMessage';
import HeaderActions from '../../components/public-tracking/HeaderActions';
import ShareSection from '../../components/public-tracking/ShareSection';
import ShipmentDetails from '../../components/public-tracking/ShipmentDetails';
import CurrentStatus from '../../components/public-tracking/CurrentStatus';
import TrackingHistory from '../../components/public-tracking/TrackingHistory';

// Import new enhanced components
import DeliveryProgressBar from '../../components/public-tracking/DeliveryProgressBar';

const API_URL = process.env.REACT_APP_PUBLIC_TRACKING_API_GATEWAY_URL;
const LOTTIE_SRC = "/animations/loader.lottie";

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const containerTransition = {
  duration: 0.3,
  ease: "easeInOut"
};

export default function PublicTrackingPage() {
  const [awb, setAwb] = useState('');
  const [status, setStatus] = useState('idle');
  const [data, setData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Memoized tracking history
  const trackingHistory = useMemo(() => {
    if (!data?.order_processing_business_status_codes) return [];
    
    return [...data.order_processing_business_status_codes].sort(
      (a, b) => new Date(b.order_status_date_time_ist) - new Date(a.order_status_date_time_ist)
    );
  }, [data]);

  const latestStatus = trackingHistory[0] || {};

  // Format date helper
  const formatDate = useCallback((dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return dateString;
    }
  }, []);

  // Initialize AWB from URL
  useEffect(() => {
    const awbFromURL = searchParams.get('awb');
    if (awbFromURL && awbFromURL !== awb) {
      setAwb(awbFromURL);
      handleSearch(awbFromURL);
    }
  }, [searchParams]);

  // Debounced search handler
  const handleSearch = useCallback(async (awbOverride = null) => {
    const currentAwb = (awbOverride || awb.trim()).toUpperCase();
    if (!currentAwb) {
      setStatus('idle');
      return;
    }

    if (isSearching) return;

    setIsSearching(true);
    setStatus('loading');
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const res = await axios.post(
        API_URL,
        { awb_number: currentAwb },
        { 
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          timeout: 30000
        }
      );

      clearTimeout(timeoutId);

      if (res.status === 200 && res.data) {
        setData(res.data);
        setStatus('success');
        setSearchParams({ awb: currentAwb });
      } else {
        setStatus('notfound');
      }
    } catch (err) {
      console.error('Tracking search error:', err);
      if (axios.isCancel(err)) {
        setStatus('error');
      } else if (err.response?.status === 400 || err.response?.status === 404) {
        setStatus('notfound');
      } else {
        setStatus('error');
      }
    } finally {
      setIsSearching(false);
    }
  }, [awb, isSearching, setSearchParams]);

  // Reset function
  const reset = useCallback(() => {
    setAwb('');
    setStatus('idle');
    setData(null);
    setSearchParams({});
    setCopySuccess(false);
  }, [setSearchParams]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }, []);

  // Handle Enter key
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  }, [handleSearch, isSearching]);

  // Memoized share URLs
  const shareUrls = useMemo(() => {
    // Construct the complete tracking URL with AWB parameter
    const baseUrl = window.location.origin + window.location.pathname;
    const trackingUrl = `${baseUrl}?awb=${awb}`;
    
    const encodedUrl = encodeURIComponent(trackingUrl);
    const message = encodeURIComponent(`Track your order (AWB: ${awb}) here: ${trackingUrl}`);
    
    return {
      whatsapp: `https://api.whatsapp.com/send?text=${message}`,
      email: `mailto:?subject=Track your Order - AWB: ${awb}&body=Track your shipment here: ${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${message}`
    };
  }, [awb]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <TrackingHeader />
      </div>

      {/* Main Content - Flex grow to fill available space */}
      <div className="flex-1">
        <AnimatePresence mode="wait">
          {status === 'idle' && (
            <SearchForm
              key="search"
              awb={awb}
              setAwb={setAwb}
              onSearch={() => handleSearch()}
              isSearching={isSearching}
              onKeyPress={handleKeyPress}
            />
          )}

          {status === 'loading' && (
            <LoadingState
              key="loading"
              lottieSrc={LOTTIE_SRC}
            />
          )}

          {status === 'success' && data && (
            <motion.main
              key="result"
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={containerTransition}
              className="container mx-auto px-4 py-6 max-w-6xl space-y-6"
            >
              {/* Header Actions */}
              <HeaderActions
                awbNumber={data.awb_number}
                onReset={reset}
              />

              {/* NEW: Delivery Progress Bar */}
              <DeliveryProgressBar 
                trackingHistory={trackingHistory}
                latestStatus={latestStatus}
              />

              {/* Main Content Grid - Compact & Seamless */}
              <div className="grid grid-cols-1 xl:grid-cols-5 gap-0">
                {/* Shipment Details - Takes 3 columns */}
                <div className="xl:col-span-3">
                  <ShipmentDetails data={data} />
                </div>
                
                {/* Current Status - Takes 2 columns */}
                <div className="xl:col-span-2">
                  <CurrentStatus 
                    latestStatus={latestStatus}
                    formatDate={formatDate}
                  />
                </div>
              </div>

              {/* Tracking History - Full width, compact */}
              <TrackingHistory
                trackingHistory={trackingHistory}
                formatDate={formatDate}
              />

              {/* Share Section with ID for auto-scroll */}
              <div id="share-section">
                <ShareSection
                  shareUrls={shareUrls}
                  copyToClipboard={copyToClipboard}
                  copySuccess={copySuccess}
                />
              </div>
            </motion.main>
          )}

          {(status === 'notfound' || status === 'error') && (
            <ErrorMessage
              key="error"
              status={status}
              awb={awb}
              onRetry={reset}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Footer - Fixed at bottom */}
      <TrackingFooter />

      {/* Optimized Global Styles */}
      <style jsx global>{`
        body {
          margin: 0;
          padding: 0;
        }
        
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e0 #f7fafc;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f7fafc;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        
        /* Compact text utilities */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Optimized focus styles */
        .focus-visible:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 1px;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
        
        /* Compact container max-width for better fit */
        @media (min-width: 1280px) {
          .container {
            max-width: 1200px;
          }
        }

        /* Animation for feedback trigger */
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0,-30px,0);
          }
          70% {
            transform: translate3d(0,-15px,0);
          }
          90% {
            transform: translate3d(0,-4px,0);
          }
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }

        /* Pulse animation for progress indicators */
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: .5;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        /* Ping animation for live indicators */
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        
        .animate-ping {
          animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}